import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Headphones, Lock } from 'lucide-react';
import { Card } from '../ui/card';

const fmtTime = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

/**
 * Lightweight meditation audio player for the E-course.
 * Plays one of N tracks from the supplied list. Locked tracks (no audio_url)
 * render in a disabled state so the UI can preview upcoming content.
 *
 * Usage:
 *   <MeditationPlayer
 *      title="The 8 Emotional Set Point Meditations"
 *      subtitle="Choose the set point you're working with."
 *      tracks={[{ id, title, audio_url }, ...]}
 *   />
 */
export const MeditationPlayer = ({ title, subtitle, tracks = [], compact = false }) => {
  const audioRef = useRef(null);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const activeTrack = tracks.find((t) => t.id === activeTrackId);

  const playTrack = (track) => {
    if (!track?.audio_url) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.src !== track.audio_url) {
      audio.src = track.audio_url;
    }
    audio.currentTime = track.start_seconds || 0;
    audio.play().then(() => {
      setActiveTrackId(track.id);
      setPlaying(true);
    }).catch(() => {});
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().then(() => setPlaying(true));
    else { audio.pause(); setPlaying(false); }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onDuration = () => setDuration(audio.duration);
    const onEnded = () => setPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onDuration);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onDuration);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  return (
    <Card
      className={`bg-card/70 border-secondary/25 ${compact ? 'p-4' : 'p-6'}`}
      data-testid={`meditation-player-${(title || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <Headphones className="w-5 h-5 mt-1 text-secondary shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className={`font-heading text-foreground ${compact ? 'text-lg' : 'text-xl'}`}>{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      </div>

      {tracks.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No tracks available yet.</p>
      ) : (
        <ul className="space-y-2">
          {tracks.map((track, idx) => {
            const isActive = activeTrackId === track.id;
            const isLocked = !track.audio_url;
            return (
              <li key={track.id}>
                <button
                  onClick={() => (isActive ? toggle() : playTrack(track))}
                  disabled={isLocked}
                  className={`w-full flex items-center gap-3 text-left rounded-lg px-3 py-2.5 transition-colors ${
                    isActive ? 'bg-secondary/15 border border-secondary/40' :
                    isLocked ? 'bg-card/20 border border-silver/10 opacity-60 cursor-not-allowed' :
                    'bg-card/40 border border-silver/15 hover:bg-card/70'
                  }`}
                  data-testid={`meditation-track-${track.id}`}
                >
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                    isActive ? 'bg-secondary text-secondary-foreground' :
                    'bg-card text-muted-foreground border border-silver/30'
                  }`}>
                    {isLocked ? <Lock className="w-3 h-3" /> :
                     isActive && playing ? <Pause className="w-3 h-3" /> :
                     <Play className="w-3 h-3 ml-0.5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isActive ? 'text-secondary' : 'text-foreground'}`}>
                      {tracks.length > 1 && `${idx + 1}. `}{track.title}
                    </p>
                    {isLocked && <p className="text-[11px] text-muted-foreground">Coming soon</p>}
                  </div>
                  {isActive && !isLocked && (
                    <span className="text-[11px] text-secondary font-mono shrink-0">
                      {fmtTime(currentTime)} / {fmtTime(duration)}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <audio ref={audioRef} preload="none" />
    </Card>
  );
};

export default MeditationPlayer;
